<template>
  <!-- Static on phones, so the dropdown positions against the (sticky) header
       and spans the screen instead of overflowing off the left edge -->
  <div ref="dropdownContainer" class="sm:relative">
    <!-- Bell Icon with Badge (own positioning box, for the badge) -->
    <div class="relative">
      <StwButton
        size="sm"
        class="!h-10 !min-h-0 !w-10 !bg-transparent !p-0 hover:!bg-white/10"
        aria-label="Notifications"
        @click="toggle"
      >
        <i class="pi pi-bell text-lg" />
      </StwButton>
      <!-- Ring in the header colour makes the badge look cut out of the button -->
      <span
        v-if="badgeCount > 0"
        class="ring-header absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[11px] font-bold text-white ring-2"
      >
        {{ badgeCount > 99 ? '99+' : badgeCount }}
      </span>
    </div>

    <!-- Custom Dropdown -->
    <Transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="opacity-0 scale-95"
      enter-to-class="opacity-100 scale-100"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="opacity-100 scale-100"
      leave-to-class="opacity-0 scale-95"
    >
      <div
        v-if="isOpen"
        class="bg-surface-800 absolute inset-x-3 top-full z-50 mt-2 origin-top rounded-lg shadow-xl ring-1 ring-white/10 sm:inset-x-auto sm:right-0 sm:w-80 sm:origin-top-right"
      >
        <!-- Header -->
        <div
          class="border-surface-700 flex items-center justify-between border-b p-3"
        >
          <h3 class="text-sm font-semibold text-white">Notifications</h3>
          <button
            v-if="unreadCount > 0"
            class="text-primary-400 hover:text-primary-300 text-xs"
            @click="handleMarkAllRead"
          >
            Mark all read
          </button>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="p-4 text-center text-sm opacity-60">
          Loading...
        </div>

        <!-- Empty State -->
        <div
          v-else-if="invitations.length === 0 && notifications.length === 0"
          class="p-4 text-center text-sm opacity-60"
        >
          <p class="text-sm text-white/60">Nothing here yet</p>
        </div>

        <!-- List -->
        <div v-else class="max-h-96 overflow-y-auto">
          <!-- Invitations — pinned to the top, not affected by mark-as-read -->
          <div
            v-for="invitation in invitations"
            :key="`inv-${invitation.id}`"
            class="border-surface-700 border-b p-3 last:border-b-0"
          >
            <div class="flex items-start gap-3">
              <img
                :src="
                  invitation.inviter.avatar ||
                  'https://static.scan-the-world.com/assets/STW_BLACK_BG.png'
                "
                :alt="invitation.inviter.username"
                class="h-8 w-8 flex-shrink-0 rounded-md object-cover ring-1 ring-white/10"
              />
              <div class="min-w-0 flex-1">
                <p class="text-sm leading-tight">
                  <strong>{{ invitation.inviter.username }}</strong>
                  {{
                    invitation.type === 'item_share'
                      ? 'shared a model with you'
                      : 'invited you to a group'
                  }}
                </p>
                <p class="mt-0.5 truncate text-xs opacity-60">
                  {{
                    invitation.type === 'item_share'
                      ? invitation.item?.name
                      : invitation.group?.name
                  }}
                </p>
                <p class="mt-0.5 text-xs opacity-40">
                  {{ formatTimeAgo(invitation.created_at) }}
                </p>

                <div
                  v-if="!processingIds.has(invitation.id)"
                  class="mt-2 flex gap-2"
                >
                  <button
                    class="rounded-md bg-green-600 px-3 py-1 text-xs font-medium text-white transition hover:bg-green-500"
                    @click.stop="handleAccept(invitation)"
                  >
                    Accept
                  </button>
                  <button
                    class="hover:bg-surface-600 bg-surface-700 rounded-md px-3 py-1 text-xs font-medium text-white/80 transition"
                    @click.stop="handleDecline(invitation)"
                  >
                    Decline
                  </button>
                </div>
                <div v-else class="mt-2">
                  <span class="text-xs opacity-50">Processing...</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Notifications -->
          <component
            :is="formatNotification(notification).href ? 'RouterLink' : 'div'"
            v-for="notification in notifications"
            :key="notification.id"
            :to="formatNotification(notification).href ?? undefined"
            class="border-surface-700 hover:bg-surface-700/50 block cursor-pointer border-b p-3 last:border-b-0"
            @click="handleNotificationClick(notification)"
          >
            <div class="flex items-start gap-3">
              <i
                :class="formatNotification(notification).icon"
                class="mt-0.5 flex-shrink-0 text-sm opacity-70"
              />
              <div class="min-w-0 flex-1">
                <p
                  class="text-sm leading-tight"
                  :class="{ 'font-semibold': !notification.is_read }"
                >
                  {{ formatNotification(notification).text }}
                </p>
                <p class="mt-0.5 text-xs opacity-40">
                  {{ formatTimeAgo(notification.created_at) }}
                </p>
              </div>
              <span
                v-if="!notification.is_read"
                class="bg-primary-500 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full"
              />
            </div>
          </component>
        </div>

        <!-- See All -->
        <div class="border-surface-700 border-t p-2">
          <RouterLink
            to="/settings?tab=notifications"
            class="text-primary-400 hover:text-primary-300 block rounded-md p-2 text-center text-sm"
            @click="isOpen = false"
          >
            See all
          </RouterLink>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import type { NotificationResponse } from '@stw/types';
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';

import {
  getNotifications,
  getUnreadNotificationCount,
  markAllNotificationsRead,
  markNotificationRead,
} from '@/api/notification';
import StwButton from '@/components/stw/StwButton.vue';
import { usePendingInvitations } from '@/composables/usePendingInvitations';
import { formatNotification } from '@/utils/notificationFormat';

const dropdownContainer = ref<HTMLElement>();
const isOpen = ref(false);

const loading = ref(false);
const unreadCount = ref(0);
const notifications = ref<NotificationResponse[]>([]);

const {
  invitations,
  processingIds,
  loadInvitations,
  handleAccept,
  handleDecline,
} = usePendingInvitations();

// getPendingInvitations() is unpaginated, so its length is always the true
// count — no separate count endpoint needed once the list has been fetched.
const badgeCount = computed(() => unreadCount.value + invitations.value.length);

const toggle = async () => {
  isOpen.value = !isOpen.value;
  if (isOpen.value) {
    await Promise.all([
      loadNotifications(),
      loadInvitations(),
      loadUnreadCount(),
    ]);
  }
};

const handleClickOutside = (event: MouseEvent) => {
  if (
    isOpen.value &&
    dropdownContainer.value &&
    !dropdownContainer.value.contains(event.target as Node)
  ) {
    isOpen.value = false;
  }
};

const loadNotifications = async () => {
  loading.value = true;
  try {
    const result = await getNotifications(1, 10);
    notifications.value = result.notifications;
  } catch (error) {
    console.error('Failed to load notifications:', error);
  } finally {
    loading.value = false;
  }
};

const loadUnreadCount = async () => {
  try {
    const result = await getUnreadNotificationCount();
    unreadCount.value = result.count;
  } catch (error) {
    console.error('Failed to load unread notification count:', error);
  }
};

const handleNotificationClick = async (notification: NotificationResponse) => {
  if (!notification.is_read) {
    notification.is_read = true;
    unreadCount.value = Math.max(0, unreadCount.value - 1);
    try {
      await markNotificationRead(notification.id);
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  }
  isOpen.value = false;
};

const handleMarkAllRead = async () => {
  notifications.value = notifications.value.map((n) => ({
    ...n,
    is_read: true,
  }));
  unreadCount.value = 0;
  try {
    await markAllNotificationsRead();
  } catch (error) {
    console.error('Failed to mark all notifications as read:', error);
  }
};

const formatTimeAgo = (date: string | Date) => {
  const now = new Date();
  const diffMs = now.getTime() - new Date(date).getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
};

onMounted(() => {
  // Fetch the real invitation list (not just a count) even before the
  // dropdown is opened, so its length can drive the badge immediately —
  // pending invites are always few, so this is cheap.
  loadInvitations();
  loadUnreadCount();
  document.addEventListener('click', handleClickOutside);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>
